/**
 * Compact spend-vs-available status for a cost-control line.
 * Shows Over / Unmapped / OK so users see breaches before submit.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Space, Tag, Tooltip, Typography } from 'antd';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export type BudgetSpendStatusProps = {
  projectId?: string | null;
  wbsNodeId?: string | null;
  cbsCodeId?: string | null;
  costCategory?: string | null;
  /** Line amount to compare against Available */
  requestedAmount?: number | null;
  /** Exclude this document's existing commitments (PO amend / re-check) */
  excludeSourceType?: string;
  excludeSourceId?: string;
  compact?: boolean;
};

export const BudgetSpendStatus: React.FC<BudgetSpendStatusProps> = ({
  projectId,
  wbsNodeId,
  cbsCodeId,
  costCategory,
  requestedAmount,
  excludeSourceType,
  excludeSourceId,
  compact,
}) => {
  const amount = Number(requestedAmount) || 0;
  const enabled = !!projectId && amount > 0;

  const q = useQuery({
    queryKey: [
      'budget-spend-status',
      projectId,
      wbsNodeId,
      cbsCodeId,
      costCategory,
      amount,
      excludeSourceType,
      excludeSourceId,
    ],
    queryFn: () =>
      projectService.checkCostControl(String(projectId), {
        lines: [
          {
            projectId,
            wbsNodeId: wbsNodeId || null,
            cbsCodeId: cbsCodeId || null,
            costCategory: costCategory || 'Material',
            amount,
          },
        ],
        excludeSourceType,
        excludeSourceId,
      }),
    enabled,
    staleTime: 10_000,
  });

  if (!enabled) {
    if (!projectId) return compact ? null : <Text type="secondary">—</Text>;
    return compact ? null : (
      <Text type="secondary" style={{ fontSize: 12 }}>
        No amount
      </Text>
    );
  }

  if (q.isLoading) {
    return compact ? null : (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Checking…
      </Text>
    );
  }
  if (q.isError || !q.data) return null;

  const d = q.data as any;
  const estimationMode = d.mode === 'estimation';
  const limitLabel = estimationMode ? 'estimated cost' : 'budget';

  if (!d.active) {
    return compact ? null : (
      <Tag style={{ margin: 0 }}>No cost control</Tag>
    );
  }

  if (d.ok) {
    const avail = Number(d.totals?.available);
    return (
      <Tooltip
        title={`Within available ${limitLabel}${Number.isFinite(avail) ? ` (${formatMoney(avail)} left for this check)` : ''}`}
      >
        <Tag color="success" style={{ margin: 0, fontSize: compact ? 11 : 12 }}>
          {compact ? 'OK' : estimationMode ? 'Within estimate' : 'Within budget'}
        </Tag>
      </Tooltip>
    );
  }

  const breach = (d.breaches || [])[0] as
    | { message?: string; over?: number; available?: number; unmapped?: boolean }
    | undefined;
  const over = Number(breach?.over) || Number(d.totals?.over) || 0;
  const unmapped =
    !estimationMode &&
    (!!breach?.unmapped || /WBS is required|No Current Budget/i.test(String(breach?.message || '')));

  if (unmapped) {
    return (
      <Tooltip title={breach?.message || 'No matching budget line or WBS missing'}>
        <Tag color="error" style={{ margin: 0, fontSize: compact ? 11 : 12 }}>
          {compact ? 'Unmapped' : 'No budget line'}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={breach?.message || `Exceeds available ${limitLabel} by ${formatMoney(over)}`}>
      <Space size={4} wrap>
        <Tag color="error" style={{ margin: 0, fontSize: compact ? 11 : 12 }}>
          Over {formatMoney(over)}
        </Tag>
        {!compact && (
          <Text type="danger" style={{ fontSize: 12 }}>
            {estimationMode ? 'Exceeds estimate' : 'Exceeds budget'}
          </Text>
        )}
      </Space>
    </Tooltip>
  );
};

export default BudgetSpendStatus;
